import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

export interface ProfitMargins {
  markupPercent: number;
  subtotalCost: number;
  sellingPrice: number;
  markupAmount: number;
}

export interface ProfitWarningProps {
  margins: ProfitMargins;
  showInDialog?: boolean;
  onClose?: () => void;
  onProceedAnyway?: () => void;
  variant?: 'inline' | 'banner' | 'dialog';
  context?: 'estimate' | 'changeOrder';
}

// Configuration for warning thresholds based on industry standards
const WARNING_THRESHOLDS = {
  MINIMUM_MARKUP: 5, // Below 5% markup is concerning
  INDUSTRY_STANDARD_MARKUP: 15, // 15%+ markup is industry standard
  GOOD_MARKUP: 20, // 20%+ markup is healthy for business
};

export const calculateWarningLevel = (
  margins: ProfitMargins
): 'none' | 'info' | 'warning' | 'critical' => {
  const { markupPercent } = margins;

  // Critical - below minimum viable markup
  if (markupPercent < WARNING_THRESHOLDS.MINIMUM_MARKUP) {
    return 'critical';
  }

  // Warning - below industry standard
  if (markupPercent < WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP) {
    return 'warning';
  }

  // Good markup - no warning needed
  if (markupPercent >= WARNING_THRESHOLDS.GOOD_MARKUP) {
    return 'none';
  }

  // Info level for adequate but not great markup
  return 'info';
};

export const getWarningMessages = (
  margins: ProfitMargins,
  context: string = 'estimate'
): {
  title: string;
  messages: string[];
  recommendations: string[];
} => {
  const { markupPercent, subtotalCost, sellingPrice } = margins;
  const warningLevel = calculateWarningLevel(margins);

  const messages: string[] = [];
  const recommendations: string[] = [];

  if (warningLevel === 'critical') {
    messages.push(
      `Very low markup: ${markupPercent.toFixed(1)}% (below ${WARNING_THRESHOLDS.MINIMUM_MARKUP}%)`
    );
    recommendations.push(
      `Increase selling price to achieve at least ${WARNING_THRESHOLDS.MINIMUM_MARKUP}% markup for business viability`
    );
    const suggestedPrice = subtotalCost * (1 + WARNING_THRESHOLDS.MINIMUM_MARKUP / 100);
    recommendations.push(
      `Suggested minimum selling price: ${formatCurrency(suggestedPrice)} (current: ${formatCurrency(sellingPrice)})`
    );
  } else if (warningLevel === 'warning') {
    messages.push(
      `Below industry standard markup: ${markupPercent.toFixed(1)}% (recommended ${WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP}%+)`
    );
    recommendations.push(
      `Consider increasing selling price to achieve ${WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP}-${WARNING_THRESHOLDS.GOOD_MARKUP}% markup for healthy margins`
    );
    const suggestedPrice = subtotalCost * (1 + WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP / 100);
    recommendations.push(
      `Suggested selling price: ${formatCurrency(suggestedPrice)} for ${WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP}% markup`
    );
  } else if (warningLevel === 'info') {
    messages.push(
      `Acceptable markup: ${markupPercent.toFixed(1)}% (industry standard is ${WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP}%+)`
    );
    recommendations.push(
      `Consider targeting ${WARNING_THRESHOLDS.GOOD_MARKUP}%+ markup for optimal profitability`
    );
  }

  const title =
    warningLevel === 'critical'
      ? `Markup Needs Attention`
      : warningLevel === 'warning'
        ? `Consider Higher Markup`
        : `Markup Information`;

  return { title, messages, recommendations };
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const ProfitWarning: React.FC<ProfitWarningProps> = ({
  margins,
  showInDialog = false,
  onClose,
  onProceedAnyway,
  variant = 'inline',
  context = 'estimate',
}) => {
  const theme = useTheme();
  const warningLevel = calculateWarningLevel(margins);
  const { title, messages, recommendations } = getWarningMessages(margins, context);

  if (warningLevel === 'none') {
    return null;
  }

  const getAlertSeverity = () => {
    switch (warningLevel) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  };

  const getIcon = () => {
    switch (warningLevel) {
      case 'critical':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const InlineWarning = () => (
    <Alert
      severity={getAlertSeverity()}
      icon={getIcon()}
      sx={{
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
        // Enhanced theme-aware styling for better contrast
        ...(warningLevel === 'critical' && {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.error.dark + '40' // Semi-transparent error dark for dark mode
              : theme.palette.error.light + '20', // Light error tint for light mode
          borderLeft: `4px solid ${theme.palette.error.main}`,
          border: `1px solid ${theme.palette.error.main}40`, // Semi-transparent border
        }),
        ...(warningLevel === 'warning' && {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.warning.dark + '40'
              : theme.palette.warning.light + '20',
          borderLeft: `4px solid ${theme.palette.warning.main}`,
          border: `1px solid ${theme.palette.warning.main}40`,
        }),
      }}
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{title}</AlertTitle>

      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}
        >
          Current Margins:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`Markup: ${margins.markupPercent.toFixed(1)}%`}
            color={
              margins.markupPercent < WARNING_THRESHOLDS.MINIMUM_MARKUP
                ? 'error'
                : margins.markupPercent < WARNING_THRESHOLDS.INDUSTRY_STANDARD_MARKUP
                  ? 'warning'
                  : margins.markupPercent >= WARNING_THRESHOLDS.GOOD_MARKUP
                    ? 'success'
                    : 'default'
            }
            size="small"
            sx={{
              fontWeight: 600,
              // Enhanced contrast for negative markup values
              ...(margins.markupPercent < 0 && {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.error.dark
                    : theme.palette.error.main,
                color: theme.palette.error.contrastText,
                '& .MuiChip-label': {
                  color: 'inherit',
                },
              }),
            }}
          />
          <Chip
            label={`Subtotal: ${formatCurrency(margins.subtotalCost)}`}
            size="small"
            sx={{
              backgroundColor:
                theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
              color: theme.palette.text.primary,
            }}
          />
          <Chip
            label={`Selling Price: ${formatCurrency(margins.sellingPrice)}`}
            color="primary"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {messages.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
            >
              Issues Found:
            </Typography>
            <ul
              style={{
                margin: 0,
                paddingLeft: '20px',
                color: theme.palette.text.primary,
              }}
            >
              {messages.map((message, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    {message}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}

        {recommendations.length > 0 && (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
            >
              Recommendations:
            </Typography>
            <ul
              style={{
                margin: 0,
                paddingLeft: '20px',
                color: theme.palette.text.primary,
              }}
            >
              {recommendations.map((rec, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                    {rec}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}

        {warningLevel === 'critical' && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.warning.dark
                  : theme.palette.warning.light,
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.warning.contrastText
                  : theme.palette.warning.dark,
              borderRadius: 1,
              border: `1px solid ${theme.palette.warning.main}`,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Pricing Recommendation
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Consider increasing the selling price to achieve a healthier markup that covers
              business costs and ensures profitability.
            </Typography>
          </Box>
        )}
      </Box>
    </Alert>
  );

  const DialogWarning = () => (
    <Dialog
      open={showInDialog}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          ...(warningLevel === 'critical' && {
            border: `3px solid ${theme.palette.error.main}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 0 20px ${theme.palette.error.main}40`
                : `0 0 20px ${theme.palette.error.main}30`,
          }),
          ...(warningLevel === 'warning' && {
            border: `2px solid ${theme.palette.warning.main}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 0 15px ${theme.palette.warning.main}30`
                : `0 0 15px ${theme.palette.warning.main}20`,
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor:
            warningLevel === 'critical' ? theme.palette.error.main : theme.palette.warning.main,
          color:
            warningLevel === 'critical'
              ? theme.palette.error.contrastText
              : theme.palette.warning.contrastText,
          pr: 1,
          // Ensure proper text contrast in all themes
          '& .MuiTypography-root': {
            color: 'inherit',
          },
          '& .MuiIconButton-root': {
            color: 'inherit',
            '&:hover': {
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
          },
        }}
      >
        {getIcon()}
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
          {title}
        </Typography>
        {onClose && (
          <IconButton aria-label="close" onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <InlineWarning />

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}
          >
            Financial Summary:
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            • Subtotal cost: {formatCurrency(margins.subtotalCost)}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            • Markup amount: {formatCurrency(margins.markupAmount)}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            • Markup percentage: {margins.markupPercent.toFixed(1)}%
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mt: 1, color: theme.palette.text.primary }}
          >
            • Selling price: {formatCurrency(margins.sellingPrice)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {onClose && (
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: 500,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.dark + '20'
                    : theme.palette.primary.light + '30',
              },
            }}
          >
            Review & Edit Margins
          </Button>
        )}
        {onProceedAnyway && warningLevel !== 'critical' && (
          <Button
            onClick={onProceedAnyway}
            color="warning"
            variant="outlined"
            sx={{
              fontWeight: 500,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.warning.dark + '20'
                    : theme.palette.warning.light + '30',
              },
            }}
          >
            Proceed with Current Margins
          </Button>
        )}
        {onProceedAnyway && warningLevel === 'critical' && (
          <Button
            onClick={onProceedAnyway}
            color="error"
            variant="contained"
            sx={{
              fontWeight: 700,
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              },
              // Add a subtle warning animation
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: `0 0 0 0 ${theme.palette.error.main}40`,
                },
                '70%': {
                  boxShadow: `0 0 0 10px ${theme.palette.error.main}00`,
                },
                '100%': {
                  boxShadow: `0 0 0 0 ${theme.palette.error.main}00`,
                },
              },
            }}
          >
            I Understand the Risk - Proceed Anyway
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  if (variant === 'dialog') {
    return <DialogWarning />;
  }

  return <InlineWarning />;
};

export default ProfitWarning;
